import { OWNER_ENTITY_LABELS, OwnerEntity, UserRole } from '@bhops/shared';

// 다른 법인 로봇 — 콘솔 쪽 판정 한 곳.
//
// 오너 결정 2026-10-20 — 직원도 허니컴로보틱스 로봇을 보고 고친다(역할 권한 그대로, VIEWER 는
// 읽기만). 서버 정본은 apps/api/src/common/abilities/robot-scope.ts 다. 그래서 로봇 화면의 편집·
// 삭제·상태 정정·회수 같은 버튼은 **로봇의 법인을 보지 않는다** — 비욘드허니컴 로봇과 똑같이 뜬다.
//
// 오너 결정 2026-10-21 — 허니컴로보틱스 로봇도 비욘드허니컴 고객사에 배치("#14와 #124-2도
// 고객사에 배치할 수 있게"). 10-20 에는 보는 것만 넓히고 '로봇 = 매장/고객사/계약/배치 법인'
// 규칙을 남겨서, 직원 화면의 [배치]·[계약에 배정]·[계약에 연결]을 꺼 두고 교체 후보도 같은 법인
// 로봇으로만 좁혔다. 이제 **쓰는 방향도 보는 방향과 같게** 연다(서버 canUseRobotAt ·
// robotEntitiesUsableAt 의 거울이 아래 robotUsableAt · robotEntitiesUsableAt 이다):
//   · 비욘드허니컴 고객사·매장·계약·배치 ← 비욘드허니컴 로봇 · 허니컴로보틱스 로봇
//   · 허니컴로보틱스 고객사·매장·계약·배치 ← 허니컴로보틱스 로봇만
// 이 규칙은 관리자도 묶는다(관리자가 법인을 넘는 것은 '닿는' 축이지 '짝짓는' 축이 아니다).
// 로봇의 소유 법인은 이 동작들로 바뀌지 않는다(회계 장부 그대로) — 그래서 후보 목록에서 다른
// 법인 로봇에는 '허니컴로보틱스' 표시를 붙여, 남의 장부 로봇을 내보낸다는 것을 사람이 알게 한다.
//
// 콘솔의 판정은 편의일 뿐이다 — 경계는 언제나 서버다. 여기 규칙이 서버와 어긋나면 서버가 거절하고
// 그 문구(robotUseDeniedMessage 와 같은 말)가 창에 뜬다. 바꿀 때는 서버 SHARED_ROBOT_ENTITIES 와
// 아래 SHARED_ROBOT_ENTITIES 를 함께 고친다.
//
// ⚠ 이 판정을 permissions.ts 에 두지 마라. 그 파일의 헬퍼는 전부 역할 하나만 받아 canOperate 에
//   위임해야 한다(tools/check-role-unification.ts ②). 이건 역할이 아니라 **법인** 축이다.

/** 판정에 필요한 사용자 모양 — 로그인 전(null)도 받는다. */
type EntityUser = { role: UserRole; ownerEntity: OwnerEntity } | null | undefined;

/**
 * 어느 법인 직원이든 보고 쓸 수 있는 로봇의 소유 법인 — 서버 robot-scope.ts 의 같은 이름 상수와
 * 같은 값이어야 한다(오너 결정 2026-10-20 · 2026-10-21).
 */
const SHARED_ROBOT_ENTITIES: readonly OwnerEntity[] = [OwnerEntity.HONEYCOMB_ROBOTICS];

/**
 * `targetEntity` 법인의 고객사·매장·계약·배치에 쓸 수 있는 로봇의 소유 법인들.
 * 비욘드허니컴 → [비욘드허니컴, 허니컴로보틱스], 허니컴로보틱스 → [허니컴로보틱스].
 */
export function robotEntitiesUsableAt(targetEntity: OwnerEntity): OwnerEntity[] {
  const out: OwnerEntity[] = [targetEntity];
  for (const shared of SHARED_ROBOT_ENTITIES) {
    if (!out.includes(shared)) out.push(shared);
  }
  return out;
}

/**
 * `robotEntity` 소유 로봇을 `targetEntity` 법인의 고객사·매장·계약·배치에 쓸 수 있나 — 서버
 * canUseRobotAt 의 거울. 역할을 받지 않는다(관리자도 같은 규칙).
 *
 * 오너 결정 2026-10-21 — 허니컴로보틱스 로봇도 비욘드허니컴 고객사에 배치. 한쪽 방향이다:
 *   robotUsableAt(허니컴로보틱스, 비욘드허니컴) = true
 *   robotUsableAt(비욘드허니컴, 허니컴로보틱스) = false
 */
export function robotUsableAt(robotEntity: OwnerEntity, targetEntity: OwnerEntity): boolean {
  return robotEntitiesUsableAt(targetEntity).includes(robotEntity);
}

/** 이 로봇을 받을 수 있는 고객 쪽 법인들 — robotUsableAt 을 반대 방향에서 본 목록. */
export function targetEntitiesForRobot(robotEntity: OwnerEntity): OwnerEntity[] {
  return (Object.values(OwnerEntity) as OwnerEntity[]).filter((t) => robotUsableAt(robotEntity, t));
}

/**
 * robotUsableAt 이 거짓일 때 사람에게 보일 문구 — 서버 robotUseDeniedMessage 와 **같은 말**이다
 * (화면이 미리 막을 때와 서버가 막을 때 말이 갈리지 않게).
 * 예: "비욘드허니컴 로봇은 허니컴로보틱스 고객사에 쓸 수 없습니다."
 */
export function robotUseDeniedMessage(robotEntity: OwnerEntity, targetEntity: OwnerEntity): string {
  return `${OWNER_ENTITY_LABELS[robotEntity]} 로봇은 ${OWNER_ENTITY_LABELS[targetEntity]} 고객사에 쓸 수 없습니다.`;
}

/**
 * 화면이 **고객 쪽 법인을 모를 때**(캘린더의 교체 일정처럼 응답에 고객사 법인이 실리지 않을 때)의
 * 후보 판정. 관리자가 아니면 그 사람이 다루는 고객사·배치는 언제나 자기 법인 것이므로(고객 쪽
 * 자원은 넓히지 않았다) 자기 법인을 대상으로 robotUsableAt 을 본다. 관리자는 어느 법인 고객사든
 * 다루므로 화면이 판정할 수 없다 — 거르지 않고 서버에 맡긴다(로그인 전도, 로봇의 법인 칸이 없는
 * 응답도 같다 — 모를 때는 빼지 않는다).
 */
export function robotUsableForUser(
  user: EntityUser,
  robot: { ownerEntity?: OwnerEntity | null },
): boolean {
  if (!user || user.role === UserRole.ADMIN || !robot.ownerEntity) return true;
  return robotUsableAt(robot.ownerEntity, user.ownerEntity);
}

/**
 * 이 로봇의 대상을 고르는 고객사 고르개를 어느 법인으로 좁힐지.
 *
 * 관리자가 아니면 자기 법인(서버도 그 밖의 고객사를 내려 주지 않는다). 관리자는 이 로봇을 받을 수
 * 있는 법인이 하나뿐이면(= 비욘드허니컴 로봇 → 비욘드허니컴 고객사만) 그 법인으로 좁혀, 골라도
 * 저장에서 반드시 실패할 고객사를 미리 뺀다. 여럿이면(허니컴로보틱스 로봇) 좁히지 않는다.
 * 로봇을 아직 못 읽었으면 관리자에게는 좁히지 않는다(서버가 판정한다).
 */
export function customerPickerEntityForRobot(
  user: EntityUser,
  robotEntity: OwnerEntity | null | undefined,
): OwnerEntity | undefined {
  if (user && user.role !== UserRole.ADMIN) return user.ownerEntity;
  if (!robotEntity) return undefined;
  const targets = targetEntitiesForRobot(robotEntity);
  return targets.length === 1 ? targets[0] : undefined;
}

/**
 * 후보 목록에서 로봇 이름 옆에 붙일 소유 법인 표시 — `referenceEntity`(고를 대상의 법인, 모르면
 * 내 법인)와 다른 법인 로봇에만 그 법인 이름을, 같으면 null.
 *
 * 오너 결정 2026-10-21 — 비욘드허니컴 배치·계약의 후보에 허니컴로보틱스 로봇이 섞인다. 소유 법인은
 * 바뀌지 않으므로(회계 장부 그대로) 고르는 사람이 남의 장부 로봇인 줄 알고 고르게 한다.
 */
export function otherEntityRobotLabel(
  robotEntity: OwnerEntity | null | undefined,
  referenceEntity: OwnerEntity | null | undefined,
): string | null {
  if (!robotEntity || !referenceEntity || robotEntity === referenceEntity) return null;
  return OWNER_ENTITY_LABELS[robotEntity];
}

/**
 * 관리자가 아닌 사용자가 **자기 법인이 아닌** 로봇을 보고 있나.
 *
 * 관리자는 법인을 넘으므로 언제나 false. 로그인 정보가 없으면(화면이 아직 못 읽었으면) false.
 * 2026-10-21 부터 이 판정으로 끄는 버튼은 없다 — 다른 법인 로봇의 출고·계약 기록 안내
 * (OTHER_ENTITY_HISTORY_NOTE)에만 쓴다.
 */
export function isOtherEntityRobot(user: EntityUser, robot: { ownerEntity: OwnerEntity }): boolean {
  return !!user && user.role !== UserRole.ADMIN && robot.ownerEntity !== user.ownerEntity;
}

/**
 * 관리자가 아닌 사용자가 보는 로봇이 **다른 법인 계약**에 묶여 있나(배정·사후 연결 모두).
 *
 * 로봇의 법인과 따로 본다 — 계약은 넓히지 않았으므로(2026-10-21 에도 그대로), 로봇이 어느 법인
 * 것이든 그 로봇을 붙든 계약이 내 법인 것이 아니면 '배정 해제'(서버: 계약을 못 찾음)·'계약 연결
 * 해제'(서버: 403)가 반드시 실패하고, 그 계약의 고객사 화면도 열리지 않는다. 그래서 그 두 버튼을
 * 미리 끄고 고객사 이름을 링크로 걸지 않는다.
 *
 * 계약의 법인 칸이 없으면(그 칸이 생기기 전 응답 — 서버가 콘솔보다 늦게 배포되는 창) false —
 * 모를 때는 끄지 않고 서버에 맡긴다.
 */
export function isOtherEntityContract(
  user: EntityUser,
  contract: { ownerEntity?: OwnerEntity | null } | null | undefined,
): boolean {
  return (
    !!user &&
    user.role !== UserRole.ADMIN &&
    !!contract?.ownerEntity &&
    contract.ownerEntity !== user.ownerEntity
  );
}

/** 다른 법인 계약에 묶인 로봇에서 꺼 둔 '배정 해제'·'계약 연결 해제' 버튼의 설명. */
export const OTHER_ENTITY_CONTRACT_HINT =
  '다른 법인인 허니컴로보틱스의 계약에 묶인 로봇이라 배정 해제와 계약 연결 해제는 관리자만 할 수 있습니다. 필요하면 관리자에게 요청해 주세요.';

/**
 * 다른 법인 로봇의 '출고 이력'·'계약' 탭 머리 안내.
 *
 * 로봇은 넓어졌지만 배치·계약 기록은 자기 법인 것만 내려온다. 허니컴로보틱스 이름으로 남은
 * 출고·계약은 직원 화면에서 빠지므로, 안내가 없으면 빈 목록이 "출고된 적 없음"으로 읽힌다.
 * (2026-10-21 부터 비욘드허니컴 고객사에 낸 배치는 고객사 쪽 법인으로 찍혀 직원에게 보인다 —
 * 빠지는 것은 허니컴로보틱스 이름으로 남은 옛 기록이다.)
 */
export const OTHER_ENTITY_HISTORY_NOTE =
  '다른 법인인 허니컴로보틱스의 고객사와 계약 기록은 이 화면에 나오지 않습니다.';
